import { useEffect, useState } from "react";
import useTranslations from "../../../../core/i18n/useTranslations";
import Accordion from "../../../../ui-lib/components/Accordion/Accordion";
import { getGroupRespondentsList } from "../../../../core/api/responsecenters/responsecenters";
import useUser from "../../../../core/user/useUser";
import { notifyApiErrors } from "../../../../core/helpers/helpers";
import LoadingSpinner from "../../../../ui-lib/components/Loading/LoadingSpinner";
import Table from "../../../../ui-lib/components/Tables/Table";
import TableCell from "../../../../ui-lib/components/Tables/TableCell";
import Badge from "../../../../ui-lib/components/Badges/Badge";
import { ILinkedRespondent } from "../../../../core/api/responsecenters/types";
import Dropdown from "../../../../ui-lib/components/Dropdown/Dropdown";
import { IDropdownItemsWithIcons } from "../../../../ui-lib/components/Dropdown/DropdownItem";
import { Row } from "../../../../components/Layout/Layout";
import Button from "../../../../ui-lib/components/Button/Button";
import { DeleteRespondentsFromGroupModal } from "./DeleteRespondentsFromGroupModal";
import SearchInput from "../../../../ui-lib/components/Inputs/SearchInput";
import PrimeModal from "../../../../ui-lib/components/PrimeModal/PrimeModal";
import styles from "../../../../ui-lib/components/Tables/TableRowSelectionPopup.module.css";
import classNames from "classnames";

export const GroupRespondentsAccessManager = ({
  arcId,
  groupId,
  groupName,
  shouldUpdateRespondents,
  respondentsSubmit,
}: {
  arcId: number;
  groupId: number;
  groupName: string;
  shouldUpdateRespondents: boolean;
  respondentsSubmit: (respondents: { id: number; forced: boolean }[]) => void;
}) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ page: 1, pageSize: 10 });
  const [searchText, setSearchText] = useState("");
  const [respondentsList, setRespondentsList] = useState<ILinkedRespondent[]>(
    []
  );
  const [dataToUpdate, setDataToUpdate] = useState<
    { id: number; forced: boolean; changed: boolean }[]
  >([]);
  const [selectedRespondents, setSelectedRespondents] = useState<
    ILinkedRespondent[]
  >([]);
  const [upForDelete, setUpForDelete] = useState<number[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchLinkedRespondents = async () => {
    try {
      setIsLoading(true);
      const results = await getGroupRespondentsList(
        arcId,
        groupId,
        { ...pageSettings, searchText },
        authenticatedRequest
      );
      setRespondentsList(results.data);
      setDataToUpdate(
        pageSettings.page === 1
          ? results.data.map((r) => ({
              id: r.id,
              forced: r.forcedAccess,
              changed: false,
            }))
          : [
              ...dataToUpdate,
              ...results.data.map((r) => ({
                id: r.id,
                forced: r.forcedAccess,
                changed: false,
              })),
            ]
      );
      setRespondentsList(
        pageSettings.page === 1
          ? results.data
          : [...respondentsList, ...results.data]
      );
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkedRespondents();
  }, [pageSettings.page, pageSettings.pageSize, searchText]);

  useEffect(() => {
    if (shouldUpdateRespondents) {
      const respondentsToSubmit = dataToUpdate
        .filter((d) => d.changed)
        .map((d) => ({ id: d.id, forced: d.forced }));
      if (respondentsToSubmit.length) {
        respondentsSubmit(respondentsToSubmit);
      }
    }
  }, [shouldUpdateRespondents]);

  const showMore =
    pageSettings.page * pageSettings.pageSize === respondentsList.length;

  return (
    <Accordion
      title={t("Menu:AlarmReceptions_Respondents")}
      description={t(
        "AlarmReceptions:group_respondents_list_description"
      ).replace("{name}", groupName)}
    >
      <>
        <PrimeModal
          className="batch-modal"
          isOpen={!!selectedRespondents?.length}
          customFooter={
            <Row>
              <Button
                variant="destructive"
                text={`${t("Common:delete_selection")} (${
                  selectedRespondents.length
                })`}
                onClick={() => {
                  setUpForDelete(selectedRespondents.map((i) => i.id));
                  setDeleteOpen(true);
                }}
              />
            </Row>
          }
          onClose={() => {
            setSelectedRespondents([]);
          }}
          modal={false}
          fixedWidth="640px"
          position="bottom"
        >
          <div
            className={classNames({
              [styles.popupHeader]: true,
              [styles.batchModal]: true,
            })}
          >{`${selectedRespondents?.length} ${t("Common:item_selected")}`}</div>
        </PrimeModal>
        <DeleteRespondentsFromGroupModal
          arcId={arcId}
          groupId={groupId}
          isOpen={deleteOpen}
          ids={upForDelete}
          onClose={() => setDeleteOpen(false)}
          onDelete={() => {
            setDeleteOpen(false);
            setSelectedRespondents([]);
            if (pageSettings.page === 1) {
              fetchLinkedRespondents();
            } else {
              setPageSettings({ page: 1, pageSize: 10 });
            }
          }}
        />

        <SearchInput
          placeholder={t("Administrator:administrators_search_placeholder")}
          value={searchText}
          onChange={(value) => {
            setSearchText(value);
            setPageSettings({
              ...pageSettings,
              page: 1,
            });
          }}
          limit={1}
        />
        {isLoading ? (
          <LoadingSpinner theme="primary" />
        ) : (
          <Table<ILinkedRespondent>
            columns={[
              {
                header: t("Common:username"),
                fieldTemplate: (rowData) => (
                  <TableCell value={rowData.username} />
                ),
              },
              {
                header: t("Common:status"),
                fieldTemplate: (rowData) => {
                  const badgeTitle = rowData.active
                    ? t("Administrator:active")
                    : t("Administrator:inactive");
                  const badgeVariant = rowData.active ? "success" : "secondary";

                  return <Badge title={badgeTitle} variant={badgeVariant} />;
                },
              },
              {
                header: t("Common:labels_phone_number"),
                fieldTemplate: (rowData) => (
                  <TableCell value={rowData.telephone} />
                ),
              },
              {
                header: t("Common:role_template"),
                fieldTemplate: (rowData) => (
                  <TableCell
                    leftIcon="phone-incoming"
                    iconColor="Grey-300"
                    value={
                      rowData.templateName === "LegacyAdmin"
                        ? t("Administrator:LegacyAdmin")
                        : rowData.templateName
                    }
                  />
                ),
              },
              {
                header: t("Common:access"),
                fieldTemplate: (rowData) => {
                  const dropdownItems: IDropdownItemsWithIcons[] = [
                    {
                      id: 0,
                      name: t("Common:forced"),
                      isSelected: dataToUpdate.find((d) => d.id === rowData.id)!
                        .forced,
                      icon: "check",
                      color: "Primary-700",
                      onlySelected: true,
                    },
                    {
                      id: 1,
                      name: t("Common:optional"),
                      isSelected: !dataToUpdate.find(
                        (d) => d.id === rowData.id
                      )!.forced,
                      icon: "minus",
                      color: "Grey-500",
                      onlySelected: true,
                    },
                  ];

                  return (
                    <Dropdown
                      onSelectItem={(item) => {
                        const newDateToUpdate = [...dataToUpdate];
                        const itemToChange = newDateToUpdate.find(
                          (d) => d.id === rowData.id
                        );
                        itemToChange!.forced = item.id === 0;
                        itemToChange!.changed = true;
                        setDataToUpdate(newDateToUpdate);
                      }}
                      items={dropdownItems}
                      selectedItem={dropdownItems.find((i) => i.isSelected)}
                      disabled={rowData.isAdRole}
                    />
                  );
                },
              },
            ]}
            hideEmptyMessage
            items={respondentsList}
            showRowHover
            withLazyLoading
            withShowMore
            withRowSelection
            paginatedItems={{
              items: respondentsList,
              pagination: {
                offset: 0,
                limit: respondentsList.length,
                total: respondentsList.length + (showMore ? 1 : 0),
              },
            }}
            onShowMoreClick={() => {
              setPageSettings({
                page: pageSettings.page + 1,
                pageSize: pageSettings.pageSize,
              });
            }}
            rowActionsFixed
            onRowSelectionChange={(selectedItems) => {
              const filtered = selectedItems.filter(
                (i) => !!respondentsList.find((row) => row.id === i.id)
              );
              setSelectedRespondents(filtered);
            }}
          />
        )}
      </>
    </Accordion>
  );
};
